// src/utils/authHelpers.js - Scriptus Pharma Partner
import { auth } from '../config/firebase';
import { useNavigate } from 'react-router-dom';

// Cache de token para evitar renovações desnecessárias
let tokenCache = {
  token: null,
  expires: 0,
  lastRefresh: 0
};

// Controle de logs (só erros críticos)
const isDev = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
const SILENT_MODE = !isDev; // Modo silencioso em produção

/**
 * Aguarda o usuário estar autenticado com timeout
 */
export const waitForAuth = (timeout = 10000) => {
  return new Promise((resolve, reject) => {
    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }
    
    const timeoutId = setTimeout(() => {
      unsubscribe();
      reject(new Error('Timeout aguardando autenticação'));
    }, timeout);
    
    const unsubscribe = auth.onAuthStateChanged((user) => {
      clearTimeout(timeoutId);
      unsubscribe();
      
      if (user) {
        resolve(user);
      } else {
        reject(new Error('Usuário não autenticado'));
      }
    });
  });
};

/**
 * Verifica se o token em cache ainda é válido
 */
const isTokenValid = (token) => {
  if (!token || !tokenCache.token) return false;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    return payload.exp > (now + 300); // 5 minutos de margem
  } catch (error) {
    return false;
  }
};

/**
 * ULTRA OTIMIZADO: Headers de autenticação com cache e logs mínimos
 */
export const getAuthHeaders = async (forceRefresh = false) => {
  try {
    const now = Date.now();
    
    // Verifica cache primeiro (prioridade máxima)
    if (!forceRefresh && tokenCache.token && tokenCache.expires > now) {
      if (isTokenValid(tokenCache.token)) {
        // SEM LOGS - apenas retorna do cache
        return {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenCache.token}`
        };
      }
    }
    
    // Aguarda usuário (sem logs desnecessários)
    const user = await waitForAuth();
    if (!user) throw new Error('Usuário não autenticado');
    
    // Controla frequência de renovação
    const timeSinceLastRefresh = now - tokenCache.lastRefresh;
    const shouldForceRefresh = forceRefresh || timeSinceLastRefresh > 300000; // 5 minutos
    
    // Obtém token (com ou sem renovação)
    const token = await user.getIdToken(shouldForceRefresh);
    if (!token) throw new Error('Token não disponível');
    
    // Atualiza cache silenciosamente
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      tokenCache = {
        token,
        expires: (payload.exp - 300) * 1000, // 5 min antes expiração
        lastRefresh: now
      };
    } catch (e) {
      // Fallback silencioso
      tokenCache = {
        token,
        expires: now + 3300000, // 55 minutos
        lastRefresh: now
      };
    }
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  } catch (error) {
    // Limpa cache em erro
    tokenCache = { token: null, expires: 0, lastRefresh: 0 };
    
    // Log apenas erros críticos
    if (!SILENT_MODE) {
      console.error('Erro crítico auth:', error.message);
    }
    throw error;
  }
};

/**
 * Handler para erros da API - Adaptado para Farmácia
 */
export const handleApiError = (error, navigate) => {
  const errorHandlers = {
    'TOKEN_EXPIRED': () => {
      tokenCache = { token: null, expires: 0, lastRefresh: 0 };
      if (navigate) navigate('/login');
      else window.location.href = '/login';
    },
    'SUBSCRIPTION_REQUIRED': () => {
      if (navigate) navigate('/assinatura');
      else window.location.href = '/assinatura';
    },
    'PARTNER_REQUIRED': () => {
      window.location.href = '/assinatura?upgrade=partner';
    },
    'RATE_LIMIT_EXCEEDED': () => {
      alert('Muitas tentativas. Aguarde um momento.');
    },
    'PHARMACY_REQUIRED': () => {
      alert('Funcionalidade restrita a farmácias.');
    },
    'REGISTRATION_INCOMPLETE': () => {
      if (navigate) navigate('/cadastro');
      else window.location.href = '/cadastro';
    }
  };

  const handler = errorHandlers[error.code];
  if (handler) {
    handler();
    return true;
  }
  
  return false;
};

/**
 * ULTRA OTIMIZADO: Requisições com logs mínimos
 */
export const fetchWithAuth = async (url, options = {}, maxRetries = 2) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Controla renovação de forma inteligente
      const forceRefresh = attempt > 0 || options.forceRefresh === true;
      
      // Obtém headers otimizados
      const headers = await getAuthHeaders(forceRefresh);
      
      // Faz requisição silenciosamente
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      });
      
      // Sucesso? Retorna sem logs
      if (response.ok) {
        return response;
      }
      
      // Tratamento de erros específicos
      if (response.status === 401 || response.status === 403) {
        try {
          const errorData = await response.json();
          
          if (errorData.code && ['SUBSCRIPTION_REQUIRED', 'PARTNER_REQUIRED', 'PHARMACY_REQUIRED', 'REGISTRATION_INCOMPLETE'].includes(errorData.code)) {
            const error = new Error(errorData.error || 'Erro de autorização');
            error.code = errorData.code;
            error.details = errorData.details;
            throw error;
          }
        } catch (jsonError) {
          // Continua silenciosamente
        }
      }
      
      // Rate limiting
      if (response.status === 429) {
        const errorData = await response.json();
        const error = new Error(errorData.error || 'Rate limit atingido');
        error.code = 'RATE_LIMIT_EXCEEDED';
        error.retryAfter = errorData.retryAfter;
        throw error;
      }
      
      // Retry em 401
      if (response.status === 401 && attempt < maxRetries) {
        // Limpa cache e tenta novamente (sem logs)
        tokenCache = { token: null, expires: 0, lastRefresh: 0 };
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        continue;
      }
      
      // Outros erros
      const errorText = await response.text();
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
      error.status = response.status;
      error.response = response;
      error.body = errorText;
      
      // Log apenas erros 5xx ou em desenvolvimento
      if (response.status >= 500 && !SILENT_MODE) {
        console.error(`Erro servidor ${url}: ${response.status}`);
      }
      
      throw error;
      
    } catch (error) {
      lastError = error;
      
      // Não faz retry para erros específicos
      if (error.code && ['SUBSCRIPTION_REQUIRED', 'PARTNER_REQUIRED', 'RATE_LIMIT_EXCEEDED', 'REGISTRATION_INCOMPLETE'].includes(error.code)) {
        throw error;
      }
      
      // Controle de retry
      if (!isRetryableError(error) && attempt < maxRetries) {
        break;
      }
      
      if (attempt === maxRetries) {
        // Log apenas falhas totais
        if (!SILENT_MODE) {
          console.error('Falha total:', error.message);
        }
        throw error;
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }
  
  throw lastError;
};

/**
 * Verifica se erro é recuperável
 */
function isRetryableError(error) {
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return true;
  }
  
  if (error.status && [401, 408, 429, 500, 502, 503, 504].includes(error.status)) {
    return true;
  }
  
  if (error.message && error.message.includes('timeout')) {
    return true;
  }
  
  return false;
}

/**
 * Verifica se usuário está autenticado
 */
export const isAuthenticated = () => {
  return !!auth.currentUser;
};

/**
 * Obtém token atual com cache
 */
export const getCurrentToken = async () => {
  try {
    // Verifica cache primeiro
    if (tokenCache.token && tokenCache.expires > Date.now()) {
      return tokenCache.token;
    }
    
    const user = await waitForAuth();
    if (!user) return null;
    
    const token = await user.getIdToken(true);
    
    // Atualiza cache silenciosamente
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        tokenCache = {
          token,
          expires: (payload.exp - 300) * 1000,
          lastRefresh: Date.now()
        };
      } catch (e) {
        // Fallback silencioso
      }
    }
    
    return token;
  } catch (error) {
    return null;
  }
};

/**
 * Valida token atual
 */
export const validateCurrentToken = async () => {
  try {
    const token = await getCurrentToken();
    if (!token) return false;
    
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    
    return payload.exp > (now + 300); // 5 minutos margem
  } catch (error) {
    return false;
  }
};

/**
 * Verifica se é erro de autenticação
 */
export const isAuthError = (error) => {
  const errorMessage = error.message || error.toString();
  const statusCode = error.status || error.response?.status;
  
  return (
    statusCode === 401 ||
    errorMessage.includes('401') ||
    errorMessage.includes('Unauthorized') ||
    errorMessage.includes('Token') ||
    errorMessage.includes('authentication') ||
    errorMessage.includes('auth/') ||
    errorMessage.includes('Firebase ID token')
  );
};

/**
 * Renovação de autenticação
 */
export const refreshAuth = async () => {
  try {
    const user = await waitForAuth();
    if (!user) return false;
    
    // Limpa cache e força renovação
    tokenCache = { token: null, expires: 0, lastRefresh: 0 };
    await user.getIdToken(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return await validateCurrentToken();
  } catch (error) {
    return false;
  }
};

/**
 * Hook personalizado para React - Adaptado para Farmácia
 */
export const useScriptusAuth = () => {
  const navigate = useNavigate();
  
  const handleError = (error) => {
    return handleApiError(error, navigate);
  };
  
  const fetchWithErrorHandling = async (url, options = {}) => {
    try {
      return await fetchWithAuth(url, options);
    } catch (error) {
      const handled = handleError(error);
      if (!handled) {
        throw error;
      }
    }
  };
  
  return {
    fetchWithAuth: fetchWithErrorHandling,
    handleError
  };
};

/**
 * Debug apenas em desenvolvimento
 */
export const debugAuth = async () => {
  if (SILENT_MODE) return;
  
  console.log('=== DEBUG AUTH - SCRIPTUS PHARMA ===');
  
  try {
    console.log('Auth status:', {
      authenticated: !!auth.currentUser,
      cached: !!tokenCache.token,
      cacheValid: tokenCache.expires > Date.now()
    });
  } catch (error) {
    console.error('Debug error:', error);
  }
};